import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Copy,
  Link,
  Users,
  UserCheck,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { useToast } from '../../../hooks/useToast';
import { Spinner } from '../../ui/Spinner';
import { Badge } from '../../ui/Badge';
import { formatDateOnly } from '../../../utils/dateUtils';
import { PartnerDashboard } from '../../../types/affiliate';

interface PartnerReferral {
  id: string;
  redacted_email?: string;
  conversion_status: string;
  signed_up_at: string;
  onboarding_completed_at?: string;
  first_subscription_at?: string;
}

interface AffiliateSectionProps {
  dashboard: PartnerDashboard;
}

const ITEMS_PER_PAGE = 10;

export const AffiliateSection: React.FC<AffiliateSectionProps> = ({ dashboard }) => {
  const { showToast } = useToast();
  const [referrals, setReferrals] = useState<PartnerReferral[]>([]);
  const [referralsTotal, setReferralsTotal] = useState(0);
  const [referralsPage, setReferralsPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const referralLink = useMemo(() => {
    const origin = typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host}`
      : 'https://app.signaliq.ai';
    return `${origin}/?ref=${dashboard.partner_code}`;
  }, [dashboard.partner_code]);

  const fetchReferrals = useCallback(async (page: number) => {
    try {
      setLoading(true);
      const result = await apiClient.getMyPartnerReferrals({
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      });
      setReferrals(result.referrals);
      setReferralsTotal(result.total);
    } catch {
      showToast('Failed to load referrals', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchReferrals(referralsPage);
  }, [referralsPage, fetchReferrals]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Copied to clipboard', 'success');
    } catch {
      showToast('Failed to copy', 'error');
    }
  };

  const formatDate = (dateString: string) => formatDateOnly(dateString);

  const getStatusBadgeVariant = (status: string): 'primary-light' | 'warning' | 'success' | 'neutral' => {
    switch (status) {
      case 'signup':
        return 'primary-light';
      case 'onboarded':
        return 'warning';
      case 'subscribed':
        return 'success';
      default:
        return 'neutral';
    }
  };

  const totalPages = Math.ceil(referralsTotal / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      {/* Suspended / Inactive Banner */}
      {dashboard.status && dashboard.status !== 'active' && (
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            Your partner account is <span className="font-semibold">{dashboard.status}</span>. Referral tracking may be paused.
          </span>
        </div>
      )}

      {/* Referral Link Card */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-100">
        <div className="flex items-center gap-2 mb-3">
          <Link className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-emerald-800">Your Referral Link</span>
          <Badge variant="success" size="micro" shape="pill">
            {dashboard.partner_code}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white px-3 py-2 rounded-lg border border-emerald-200 text-sm font-mono text-neutral-700 truncate">
            {referralLink}
          </code>
          <button
            onClick={() => copyToClipboard(referralLink)}
            className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex-shrink-0"
            aria-label="Copy referral link"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-emerald-700 mt-2">
          Share this link. When users sign up through it, they are automatically attributed to you.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center">
          <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-neutral-900">{dashboard.total_referrals}</div>
          <div className="text-xs text-neutral-500">Total Referrals</div>
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center">
          <UserCheck className="w-5 h-5 text-amber-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-neutral-900">{dashboard.onboarded}</div>
          <div className="text-xs text-neutral-500">Onboarded</div>
          {dashboard.total_referrals > 0 && (
            <div className="text-xs text-amber-600 mt-0.5">
              {dashboard.conversion_rate_signup_to_onboard}%
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center">
          <CreditCard className="w-5 h-5 text-green-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-neutral-900">{dashboard.subscribed}</div>
          <div className="text-xs text-neutral-500">Subscribed</div>
          {dashboard.onboarded > 0 && (
            <div className="text-xs text-green-600 mt-0.5">
              {dashboard.conversion_rate_onboard_to_subscribe}%
            </div>
          )}
        </div>
      </div>

      {/* Referrals Table */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50">
          <h3 className="text-sm font-semibold text-neutral-900">Referrals</h3>
        </div>

        {loading ? (
          <div className="py-12">
            <Spinner size="md" text="Loading referrals..." center />
          </div>
        ) : referrals.length === 0 ? (
          <div className="py-12 text-center">
            <Users className="w-10 h-10 text-neutral-300 mx-auto mb-2" />
            <p className="text-sm text-neutral-500">No referrals yet</p>
            <p className="text-xs text-neutral-400 mt-1">Share your referral link to get started</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-100">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500">Referral</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500">Status</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500">Signed Up</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500">Onboarded</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500">Subscribed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {referrals.map((referral) => (
                    <tr key={referral.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-4 py-2.5 text-neutral-600 text-xs font-mono">
                        {referral.redacted_email || <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant={getStatusBadgeVariant(referral.conversion_status)} size="sm">
                          {referral.conversion_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-neutral-600 text-xs">
                        {formatDate(referral.signed_up_at)}
                      </td>
                      <td className="px-4 py-2.5 text-neutral-600 text-xs">
                        {referral.onboarding_completed_at
                          ? formatDate(referral.onboarding_completed_at)
                          : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-neutral-600 text-xs">
                        {referral.first_subscription_at
                          ? formatDate(referral.first_subscription_at)
                          : <span className="text-neutral-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-neutral-100 bg-neutral-50 flex items-center justify-between">
                <span className="text-xs text-neutral-500">
                  {referralsPage * ITEMS_PER_PAGE + 1}–{Math.min((referralsPage + 1) * ITEMS_PER_PAGE, referralsTotal)} of {referralsTotal}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setReferralsPage(p => Math.max(0, p - 1))}
                    disabled={referralsPage === 0}
                    className="p-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-xs text-neutral-500 px-2">
                    {referralsPage + 1} / {totalPages}
                  </span>
                  <button
                    onClick={() => setReferralsPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={referralsPage >= totalPages - 1}
                    className="p-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
